import React, { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Footer from "./home/Footer";
import EnrollmentService from "../apis/EnrollmentService";

const createInitialValues = () => ({
  name: "",
  email: "",
  phone: "",
  country: "",
  about: "",
  experiences: "",
  year: "2026",
  captchaInput: "",
});

const requiredFields = {
  name: "Name",
  email: "Email",
  about: "Tell us about yourself and your commitment to learn",
  experiences: "List your experiences with learning Arabic",
};

const generateCaptcha = () => {
  const first = Math.floor(Math.random() * 9) + 1;
  const second = Math.floor(Math.random() * 9) + 1;

  return {
    question: `What is ${first} + ${second}?`,
    answer: String(first + second),
  };
};

const Enroll = () => {
  const [values, setValues] = useState(() => createInitialValues());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [captcha, setCaptcha] = useState(() => generateCaptcha());

  const requiredFieldEntries = useMemo(() => Object.entries(requiredFields), []);

  const validate = () => {
    const nextErrors = {};

    requiredFieldEntries.forEach(([key, label]) => {
      const value = values[key]?.trim();

      if (!value) {
        nextErrors[key] = `${label} is required.`;
      }
    });

    if (values.email?.trim()) {
      const emailPattern = /.+@.+\..+/i;
      if (!emailPattern.test(values.email.trim())) {
        nextErrors.email = "Enter a valid email address.";
      }
    }

    const captchaInput = values.captchaInput?.trim();
    if (!captchaInput) {
      nextErrors.captchaInput = "Please solve the captcha.";
    } else if (captchaInput !== captcha.answer) {
      nextErrors.captchaInput = "Captcha answer is incorrect.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const { [name]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitSuccess(false);
    setSubmitError("");

    if (!validate()) {
      return;
    }

    try {
      setSubmitting(true);
      await EnrollmentService.submitEnrollment({
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        country: values.country.trim(),
        about: values.about.trim(),
        experiences: values.experiences.trim(),
        year: values.year,
      });

      setSubmitSuccess(true);
      setValues(createInitialValues());
      setErrors({});
      setCaptcha(generateCaptcha());
    } catch (error) {
      console.error("Failed to submit enrollment", error);
      setSubmitError(
        error?.response?.data?.message ||
          error?.message ||
          "We were unable to submit your enrollment. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefreshCaptcha = () => {
    setCaptcha(generateCaptcha());
    setValues((prev) => ({
      ...prev,
      captchaInput: "",
    }));
    setErrors((prev) => {
      const { captchaInput: _removed, ...rest } = prev;
      return rest;
    });
  };

  return (
    <>
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Typography variant="h3" component="h1" textAlign="center">
              Register for Tanween 2026
            </Typography>
            <Typography textAlign="center" color="text.secondary">
              Complete the enrollment form below to reserve your seat. * Indicates
              required question.
            </Typography>
          </Stack>

          {submitSuccess && (
            <Alert severity="success" onClose={() => setSubmitSuccess(false)}>
              Thank you for registering! We'll be in touch soon.
            </Alert>
          )}

          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError("")}>
              {submitError}
            </Alert>
          )}

          <Box component="form" noValidate onSubmit={handleSubmit}>
            <input type="hidden" name="year" value={values.year} readOnly />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  error={Boolean(errors.name)}
                  helperText={errors.name || ""}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  error={Boolean(errors.email)}
                  helperText={errors.email || ""}
                />
              </Grid>
              <Grid item xs={12}>
              <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={values.phone}
                  onChange={handleChange}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone || ""}
                />
              </Grid>
              <Grid item xs={12}>
              <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={values.country}
                  onChange={handleChange}
                  error={Boolean(errors.country)}
                  helperText={errors.country || ""}
                />
              </Grid>
              <Grid item xs={12}>
              <TextField
                  required
                  fullWidth
                  label="Tell us about yourself and your commitment to learn"
                  name="about"
                  multiline
                  minRows={4}
                  value={values.about}
                  onChange={handleChange}
                  error={Boolean(errors.about)}
                  helperText={errors.about || ""}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="List your experiences with learning Arabic"
                  name="experiences"
                  multiline
                  minRows={4}
                  value={values.experiences}
                  onChange={handleChange}
                  error={Boolean(errors.experiences)}
                  helperText={errors.experiences || ""}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Captcha</Typography>
                  <Typography color="text.secondary">
                    {captcha.question}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      required
                      fullWidth
                      label="Your Answer"
                      name="captchaInput"
                      value={values.captchaInput}
                      onChange={handleChange}
                      error={Boolean(errors.captchaInput)}
                      helperText={errors.captchaInput || ""}
                    />
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleRefreshCaptcha}
                      disabled={submitting}
                    >
                      New Question
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    component={RouterLink}
                    to="/"
                    variant="text"
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                  >
                    {submitting ? "Submitting…" : "Submit"}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Container>
      <Footer />
    </>
  );
};

export default Enroll;
